import { useState, useEffect } from "react";
import { Phone, X, Share, PlusSquare, Bell, Check } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { pushSupported, pushPermission, enablePush } from "@/lib/push";

const PENDING_KEY = "pula_welcome_alerts_pending";
const isIOS = () => /iP(hone|ad|od)/.test(navigator.userAgent);
const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone === true;

// One-time welcome step shown right after a member first joins, guiding them to
// switch on incoming-call alerts (and, on iPhone, add Pula to the Home Screen) so
// they're reachable from day one — even when Pula isn't open.
export function WelcomeAlertsStep() {
  const { user } = useAuth();
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!user) return;
    if (localStorage.getItem(PENDING_KEY) !== "1") return;
    // Already reachable → nothing to teach; quietly clear the step.
    if (!pushSupported() || pushPermission() === "granted") {
      localStorage.removeItem(PENDING_KEY);
      return;
    }
    // Suppress the passive bottom banner while this stronger step is on screen.
    localStorage.setItem("pula_call_notify_dismissed", "1");
    setShow(true);
  }, [user]);

  if (!show) return null;

  const iosNeedsInstall = isIOS() && !isStandalone();

  const finish = () => {
    localStorage.removeItem(PENDING_KEY);
    setShow(false);
  };

  const turnOn = async () => {
    setBusy(true);
    try {
      await enablePush();
      setDone(true);
      toast.success("You're all set — Pula can now ring you even when it's closed. 🌱");
      setTimeout(finish, 1400);
    } catch {
      toast.error("Couldn't turn on alerts. Please allow notifications for Pula in your browser.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[95] grid place-items-center p-4 bg-pula-ink/50 backdrop-blur-sm"
      data-testid="welcome-alerts-step">
      <div className="w-full max-w-md pula-card bg-white p-6 shadow-2xl border border-pula-green/25 relative animate-in fade-in zoom-in-95 duration-300">
        <button onClick={finish} aria-label="Skip" data-testid="welcome-alerts-skip"
          className="absolute top-3 right-3 text-pula-inksoft/50 hover:text-pula-ink transition-colors">
          <X className="w-5 h-5" />
        </button>

        <span className="inline-grid place-items-center w-12 h-12 rounded-full bg-pula-green/15 text-pula-green mb-4">
          <Phone className="w-6 h-6" />
        </span>
        <h2 className="font-heading font-extrabold text-2xl text-pula-ink">Be reachable from day one</h2>
        <p className="text-sm text-pula-inksoft font-body mt-1.5">
          Turn on call alerts so friends and family can reach you on Pula — even when the app is closed, just like a phone ringing.
        </p>

        {iosNeedsInstall ? (
          <div className="mt-5 space-y-3" data-testid="welcome-alerts-ios">
            <p className="text-xs font-semibold text-pula-ink">On iPhone, add Pula to your Home Screen first:</p>
            <ol className="space-y-2.5">
              <li className="flex items-center gap-3 text-sm text-pula-ink">
                <span className="shrink-0 w-7 h-7 rounded-full bg-pula-green/10 grid place-items-center text-pula-green"><Share className="w-4 h-4" /></span>
                Tap the <b>Share</b> icon in Safari
              </li>
              <li className="flex items-center gap-3 text-sm text-pula-ink">
                <span className="shrink-0 w-7 h-7 rounded-full bg-pula-green/10 grid place-items-center text-pula-green"><PlusSquare className="w-4 h-4" /></span>
                Choose <b>Add to Home Screen</b>
              </li>
              <li className="flex items-center gap-3 text-sm text-pula-ink">
                <span className="shrink-0 w-7 h-7 rounded-full bg-pula-green/10 grid place-items-center text-pula-green"><Bell className="w-4 h-4" /></span>
                Open Pula from there and turn on call alerts
              </li>
            </ol>
            <button onClick={finish} data-testid="welcome-alerts-ios-got-it"
              className="w-full mt-3 text-sm font-semibold px-4 py-2.5 rounded-full bg-pula-green text-white hover:bg-pula-greenhover transition-colors">
              Got it
            </button>
          </div>
        ) : (
          <div className="mt-6 space-y-2.5">
            <button onClick={turnOn} disabled={busy || done} data-testid="welcome-alerts-enable"
              className="w-full text-sm font-semibold px-4 py-3 rounded-full bg-pula-green text-white hover:bg-pula-greenhover transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
              {done ? (<><Check className="w-4 h-4" /> Call alerts on</>) : busy ? "Turning on…" : "Turn on call alerts"}
            </button>
            <button onClick={finish} data-testid="welcome-alerts-later"
              className="w-full text-sm font-medium text-pula-inksoft hover:text-pula-ink transition-colors">
              Maybe later
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
